import { Building2, Check, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

/** Pedidos de conexão pendentes recebidos de clínicas (organização já verificada na plataforma,
 *  achada via busca) — só vira conexão de verdade (ProsthesisLab + Partner ACTIVE) ao aceitar. */
export function LabConnectionRequestsList() {
  const utils = trpc.useUtils();
  const requestsQuery = trpc.prosthesis.lab.connectionRequests.listIncoming.useQuery();
  const accept = trpc.prosthesis.lab.connectionRequests.accept.useMutation({
    onSuccess: () => {
      toast.success("Conexão aceita — a clínica já pode enviar pedidos.");
      utils.prosthesis.lab.connectionRequests.listIncoming.invalidate();
      utils.partner.list.invalidate();
      utils.partner.countByType.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao aceitar."),
  });
  const decline = trpc.prosthesis.lab.connectionRequests.decline.useMutation({
    onSuccess: () => {
      toast.success("Pedido de conexão recusado.");
      utils.prosthesis.lab.connectionRequests.listIncoming.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao recusar."),
  });

  const requests = requestsQuery.data ?? [];
  if (requests.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-brand-200 bg-brand-50/50 dark:border-primary/30 dark:bg-primary/5">
      <div className="flex items-center justify-between gap-2 border-b border-brand-200/60 px-3 py-2 dark:border-primary/20">
        <p className="text-[12px] font-semibold text-brand-900 dark:text-primary">
          {requests.length} pedido{requests.length > 1 ? "s" : ""} de conexão pendente
          {requests.length > 1 ? "s" : ""}
        </p>
      </div>
      <ul className="divide-y divide-brand-200/40 dark:divide-primary/15">
        {requests.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[12px] sm:flex-nowrap">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-100 text-brand-700 dark:bg-primary/15 dark:text-primary">
              <Building2 className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-zinc-900 dark:text-foreground">
                {r.clinicOrganization.name}
              </p>
              <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                Pedido {formatDistanceToNow(new Date(r.createdAt), { addSuffix: true, locale: ptBR })}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={() => accept.mutate({ id: r.id })}
                disabled={accept.isPending || decline.isPending}
                className="inline-flex h-7 items-center gap-1 rounded-md bg-emerald-600 px-2.5 text-[11px] font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
              >
                <Check className="h-3 w-3" />
                Aceitar
              </button>
              <button
                type="button"
                onClick={() => decline.mutate({ id: r.id })}
                disabled={accept.isPending || decline.isPending}
                className="inline-flex h-7 items-center gap-1 rounded-md border border-red-200 px-2.5 text-[11px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/30"
              >
                <X className="h-3 w-3" />
                Recusar
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
